/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { LOGGED_TOKEN_STORAGE_KEY } from '../common/constants';

import Storage from './storage';

export type RelaySSOTarget = {
  key: 'panel' | 'quota' | 'service';
  fallbackURL: string;
  loginURL: string;
  redirect: string;
  adminOnly?: boolean;
};

export const relayPanelSSO: RelaySSOTarget = {
  key: 'panel',
  fallbackURL: 'https://panel.aiopentool.com/sign-in',
  loginURL: 'https://panel.aiopentool.com/api/user/answer-token-login',
  redirect: '/dashboard',
};

export const relayQuotaSSO: RelaySSOTarget = {
  key: 'quota',
  fallbackURL: 'https://quota.aiopentool.com/management.html',
  loginURL: 'https://quota.aiopentool.com/answer-token-login',
  redirect: '/management.html',
  adminOnly: true,
};

export const relayServiceSSO: RelaySSOTarget = {
  key: 'service',
  fallbackURL: 'https://ai.aiopentool.com/management.html',
  loginURL: 'https://ai.aiopentool.com/answer-token-login',
  redirect: '/management.html',
  adminOnly: true,
};

export const relaySSOTargets = {
  panel: relayPanelSSO,
  quota: relayQuotaSSO,
  service: relayServiceSSO,
};

export type RelaySSOTargetKey = keyof typeof relaySSOTargets;

export const isRelaySSOTargetKey = (
  value: string | null,
): value is RelaySSOTargetKey => Boolean(value && value in relaySSOTargets);

type RecoveryResponse = {
  data?: {
    access_token?: unknown;
  };
};

export const recoverAnswerSessionToken = async () => {
  try {
    const response = await fetch('/answer/api/v1/user/session-token-recovery', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      return '';
    }
    const payload = (await response.json()) as RecoveryResponse;
    const token = payload?.data?.access_token;
    if (typeof token !== 'string' || !token.trim()) {
      return '';
    }
    Storage.set(LOGGED_TOKEN_STORAGE_KEY, token.trim());
    return token.trim();
  } catch {
    return '';
  }
};

export const getOrRecoverAnswerToken = async () => {
  // The HttpOnly Answer session is the source of truth. A browser tab can keep
  // an expired bearer in localStorage long after the login cookie was renewed;
  // submitting that stale bearer to a relay leaves the user on the consumer's
  // login page even though Answer itself is still authenticated.
  const recoveredToken = await recoverAnswerSessionToken();
  if (recoveredToken) {
    return recoveredToken;
  }

  // Keep the existing bearer as a compatibility fallback for sessions created
  // before the HttpOnly recovery cookie was introduced.
  const storedToken = Storage.get(LOGGED_TOKEN_STORAGE_KEY);
  if (typeof storedToken === 'string' && storedToken.trim()) {
    return storedToken.trim();
  }
  return '';
};

export const submitRelayWithAnswerToken = (
  target: RelaySSOTarget,
  answerToken: string,
  formTarget?: string,
) => {
  // POST keeps the Answer bearer out of the URL, Referer and access logs.
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = target.loginURL;
  if (formTarget) {
    form.target = formTarget;
  }
  Object.entries({
    answer_token: answerToken,
    redirect: target.redirect,
  }).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};

const fallbackToTarget = (target: RelaySSOTarget, popup: Window | null) => {
  if (popup && !popup.closed) {
    popup.location.replace(target.fallbackURL);
    return;
  }
  window.location.assign(target.fallbackURL);
};

const restartAnswerLogin = (target: RelaySSOTarget, popup: Window | null) => {
  const loginURL = `/users/logout?relay_sso=${encodeURIComponent(target.key)}`;
  if (popup && !popup.closed) {
    popup.location.replace(loginURL);
    return;
  }
  window.location.assign(loginURL);
};

type RelaySSOHandoff =
  | { kind: 'submit'; answerToken: string }
  | { kind: 'canonical-resume' }
  | { kind: 'fallback' };

export const resolveRelaySSOHandoff = async (options: {
  recoverToken: () => Promise<string>;
  allowCanonicalResume: boolean;
  newWindow: boolean;
  hostname: string;
}): Promise<RelaySSOHandoff> => {
  // Recovery must happen on the current host. The unified-session cookie is
  // deliberately __Host- scoped and cannot survive a hostname change.
  const answerToken = await options.recoverToken();
  if (answerToken) {
    return { kind: 'submit', answerToken };
  }
  if (
    options.allowCanonicalResume &&
    !options.newWindow &&
    options.hostname === 'aiopentool.com'
  ) {
    return { kind: 'canonical-resume' };
  }
  return { kind: 'fallback' };
};

export const launchRelaySSO = async (
  target: RelaySSOTarget,
  options: {
    newWindow?: boolean;
    allowCanonicalResume?: boolean;
    forceSessionRecovery?: boolean;
  } = {},
) => {
  const {
    newWindow = false,
    allowCanonicalResume = true,
    forceSessionRecovery = false,
  } = options;
  const popupName = newWindow
    ? `answer-relay-sso-${Date.now()}-${Math.random().toString(36).slice(2)}`
    : '';
  const popup = newWindow ? window.open('', popupName) : null;
  if (popup) {
    popup.opener = null;
  }

  const handoff = await resolveRelaySSOHandoff({
    recoverToken: forceSessionRecovery
      ? recoverAnswerSessionToken
      : getOrRecoverAnswerToken,
    allowCanonicalResume,
    newWindow,
    hostname: window.location.hostname,
  });
  if (handoff.kind === 'canonical-resume') {
    const canonicalURL = new URL(window.location.href);
    canonicalURL.hostname = 'www.aiopentool.com';
    canonicalURL.searchParams.set('relay_sso', target.key);
    window.location.assign(canonicalURL.toString());
    return;
  }
  if (handoff.kind === 'fallback') {
    if (target.adminOnly) {
      restartAnswerLogin(target, popup);
      return;
    }
    fallbackToTarget(target, popup);
    return;
  }
  submitRelayWithAnswerToken(
    target,
    handoff.answerToken,
    popup ? popupName : undefined,
  );
};
